import { useState, useMemo ,useEffect} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDocuments, useVerifyDocument } from "../../../hooks/useWorkshopQueries";
import {
  Badge, PageHeader, Spinner, SearchInput, EmptyState, ErrorState,
  MetricCard, Modal, InfoRow, Avatar,
  docBadge, formatDateTime,
} from "../../../Component/Workshop/UI/index";
import {DOC_ICONS, docMeta} from "../../../Component/Workshop/UI/icons";

// ─── Config ───────────────────────────────────────────────────────────────────
const STATUS_TABS = [
  { key:"ALL",      label:"All"      },
  { key:"PENDING",  label:"Pending"  },
  { key:"APPROVED", label:"Approved" },
  { key:"REJECTED", label:"Rejected" },
];

const DOC_TYPES = [
  "All Types","RC","Insurance Policy","Driver License",
  "Fitness Certificate","Permit","Accident Photo - Front",
  "Accident Photo - Rear","Accident Photo - Left","Accident Photo - Right",
  "Accident Photo - Top","Accident Photo - Interior",
];

const SERVICE_TABS = [
  { key: "", label: "All Services" },
  { key: "workshop", label: "Workshop" },
  { key: "legal", label: "Legal" },
];

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Documents() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const globalSearch = searchParams.get("q") || "";

  const userRole = localStorage.getItem("department") || "";
  const isKam = userRole.toLowerCase() === "kam";

  // Filters
  const [activeTab,  setActiveTab]  = useState("ALL");
  const [search, setSearch] = useState(globalSearch);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [typeFilter, setTypeFilter] = useState("All Types");
  const [viewMode,   setViewMode]   = useState("table"); // "table" | "grid"
  const [serviceFilter, setServiceFilter] = useState("");

  // Modals
  const [previewDoc,  setPreviewDoc]  = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectNote,  setRejectNote]  = useState("");
  const [bulkSelected,setBulkSelected]= useState([]);

  const handleSearchChange = (value) => {
    setSearch(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value?.trim()) next.set("q", value);
        else next.delete("q");
        return next;
      },
      { replace: true }
    );
  };

   useEffect(() => {
    setIsTyping(true);
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setIsTyping(false);
    }, 600); // 600ms delay
  
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (globalSearch !== search) setSearch(globalSearch);
  }, [globalSearch]);

  // ── Real query – uncomment when backend is live ─────────────────────────────
  const filters = {};
  if (activeTab !== "ALL")        filters.status = activeTab;
  if (debouncedSearch) filters.search = debouncedSearch;
  if (typeFilter !== "All Types") filters.type   = typeFilter;
  if (serviceFilter) filters.service = serviceFilter;

  const { data, isLoading, isError, refetch } = useDocuments(filters);
  const verifyDoc = useVerifyDocument();
  const docs = data?.documents || [];
  const metrics = data?.metrics || null;

  const counts = useMemo(() => ({
    ALL:      metrics?.totalDocuments ?? docs.length,
    PENDING:  metrics?.pending ?? docs.filter(x => x.status === "PENDING").length,
    APPROVED: metrics?.approved ?? docs.filter(x => x.status === "APPROVED").length,
    REJECTED: metrics?.rejected ?? docs.filter(x => x.status === "REJECTED").length,
  }), [metrics, docs]);

  // ── Actions ─────────────────────────────────────────────────────────────────
  const handleVerify = async (docId) => {
    await verifyDoc.mutateAsync({ docId, status: "APPROVED" });
    console.log("Verify doc:", docId); // replace with mutation
  };

  const handleRejectConfirm = async () => {
    await verifyDoc.mutateAsync({ docId: rejectModal.id, status: "REJECTED", remarks: rejectNote });
    console.log("Reject doc:", rejectModal?.id, "note:", rejectNote);
    setRejectModal(null);
    setRejectNote("");
  };

  const handleBulkVerify = () => {
    bulkSelected.forEach(id => handleVerify(id));
    setBulkSelected([]);
  };

  const toggleBulk = (id) =>
    setBulkSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleAllBulk = () =>
    setBulkSelected(prev => prev.length === docs.filter(d => d.status === "PENDING").length ? [] : docs.filter(d => d.status === "PENDING").map(d => d.id));

  const pendingDocs = docs.filter(d => d.status === "PENDING");

  return (
    <div className="wd-animate" style={{ display:"flex", flexDirection:"column", gap:16 }}>

      {/* Header */}
      <PageHeader
        title="Documents"
        subtitle="Review and verify all uploaded case documents"
        actions={
          <>
            {bulkSelected.length > 0 && !isKam && (
              <div style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 12px", background:"rgba(59,130,246,.1)", borderRadius:10, border:"1px solid rgba(59,130,246,.2)" }}>
                <span style={{ fontSize:12, color:"var(--wd-blue)", fontWeight:600 }}>{bulkSelected.length} selected</span>
                <button className="wd-btn wd-btn-success wd-btn-sm" onClick={handleBulkVerify}>✓ Verify All</button>
                <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => setBulkSelected([])}>Clear</button>
              </div>
            )}
            <button className="wd-btn wd-btn-ghost">
              <DownloadIcon /> Export
            </button>
          </>
        }
      />

      {/* Metric cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
        <MetricCard label="Total Documents" value={counts.ALL}      color="#3b82f6" />
        <MetricCard label="Pending Review"  value={counts.PENDING}  color="#eab308" />
        <MetricCard label="Approved"        value={counts.APPROVED} color="#22c55e" />
        <MetricCard label="Rejected"        value={counts.REJECTED} color="#ef4444" />
      </div>

      {/* Filter bar */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {SERVICE_TABS.map((t) => (
            <button
              key={t.key}
              className={`wd-filter-tab ${serviceFilter === t.key ? "active" : ""}`}
              onClick={() => setServiceFilter(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
        {/* Status tabs */}
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          {STATUS_TABS.map(t => (
            <button key={t.key}
              className={`wd-filter-tab ${activeTab === t.key ? "active" : ""}`}
              onClick={() => { setActiveTab(t.key); setBulkSelected([]); }}
            >
              {t.label}
              <span style={{
                marginLeft:5, fontSize:10, fontWeight:700, padding:"1px 5px",
                borderRadius:10, background:"rgba(255,255,255,.1)",
              }}>{counts[t.key]}</span>
            </button>
          ))}
        </div>

        {/* Right controls */}
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          <SearchInput value={search} onChange={handleSearchChange} placeholder="Case, name, vehicle, file..." />

          <select className="wd-input wd-select" style={{ width:"auto", minWidth:160 }}
            value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          {/* View toggle */}
          <div style={{ display:"flex", gap:2 }}>
            <button className={`wd-btn wd-btn-ghost wd-btn-icon wd-btn-sm ${viewMode === "table" ? "active-view" : ""}`}
              style={viewMode === "table" ? { background:"var(--wd-hover)", color:"var(--wd-t1)" } : {}}
              onClick={() => setViewMode("table")}><ListIcon /></button>
            <button className={`wd-btn wd-btn-ghost wd-btn-icon wd-btn-sm`}
              style={viewMode === "grid" ? { background:"var(--wd-hover)", color:"var(--wd-t1)" } : {}}
              onClick={() => setViewMode("grid")}><GridIcon /></button>
          </div>
        </div>
      </div>

      {/* Bulk action bar (only shows when pending docs exist in current view) */}
      {activeTab === "PENDING" && pendingDocs.length > 0 && (
        <div style={{
          display:"flex", alignItems:"center", gap:12, padding:"10px 16px",
          background:"var(--wd-hover)", borderRadius:10, border:"1px solid var(--wd-border-md)",
        }}>
          <input type="checkbox"
            checked={bulkSelected.length === pendingDocs.length && pendingDocs.length > 0}
            onChange={toggleAllBulk}
            style={{ accentColor:"var(--wd-blue)", width:15, height:15, cursor:"pointer" }}
          />
          <span style={{ fontSize:12, color:"var(--wd-t2)" }}>
            {bulkSelected.length > 0 ? `${bulkSelected.length} of ${pendingDocs.length} selected` : `Select all ${pendingDocs.length} pending`}
          </span>
          {bulkSelected.length > 0 && (
            <>
              <button className="wd-btn wd-btn-success wd-btn-sm" onClick={handleBulkVerify}>
                ✓ Verify Selected
              </button>
              <button className="wd-btn wd-btn-danger wd-btn-sm" onClick={() => setBulkSelected([])}>
                Clear
              </button>
            </>
          )}
        </div>
      )}

      {/* Content */}
      {isLoading ? <Spinner /> :
       isError   ? <ErrorState message="Failed to load documents" onRetry={() => {}} /> :
       docs.length === 0 ? <EmptyState message="No documents match your filters" /> :
       viewMode === "table"
         ? <TableView docs={docs} onPreview={setPreviewDoc} onVerify={handleVerify} onReject={setRejectModal} onNavigate={navigate} bulkSelected={bulkSelected} onToggleBulk={toggleBulk} activeTab={activeTab} isKam={isKam}/>
         : <GridView  docs={docs} onPreview={setPreviewDoc} onVerify={handleVerify} onReject={setRejectModal} onNavigate={navigate} isKam={isKam} />
      }

      {/* Preview Modal */}
      <PreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} onVerify={handleVerify} onReject={setRejectModal} onNavigate={navigate} isKam={isKam}/>

      {/* Reject Modal */}
      <Modal open={!!rejectModal} onClose={() => { setRejectModal(null); setRejectNote(""); }} title="Reject Document"
        footer={<>
          <button className="wd-btn wd-btn-ghost" onClick={() => { setRejectModal(null); setRejectNote(""); }}>Cancel</button>
          <button className="wd-btn wd-btn-danger" onClick={handleRejectConfirm}>Reject Document</button>
        </>}
      >
        {rejectModal && (
          <>
            {/* Doc summary */}
            <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", background:"var(--wd-hover)", borderRadius:10 }}>
              <span style={{ fontSize:24 }}>{docMeta(rejectModal.type).icon}</span>
              <div>
                <div style={{ fontWeight:600, fontSize:13, color:"var(--wd-t1)" }}>{rejectModal.type}</div>
                <div style={{ fontSize:12, color:"var(--wd-t3)", fontFamily:"var(--wd-mono)" }}>{rejectModal.fileName}</div>
                <div style={{ fontSize:11, color:"var(--wd-t3)", marginTop:2 }}>{rejectModal.caseNo} · {rejectModal.customerName}</div>
              </div>
            </div>
            <div>
              <label className="wd-form-label">Reason for rejection *</label>
              <textarea className="wd-input" rows={3} style={{ resize:"vertical" }}
                value={rejectNote} onChange={e => setRejectNote(e.target.value)}
                placeholder="e.g. Document is blurry, expired, or incorrect..." />
            </div>
            {/* Quick reason chips */}
            <div>
              <label className="wd-form-label">Quick reasons</label>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {["Document blurry","Certificate expired","Wrong document uploaded","Signature missing","Details mismatch"].map(r => (
                  <button key={r} className="wd-btn wd-btn-ghost wd-btn-sm"
                    style={rejectNote === r ? { borderColor:"var(--wd-red)", color:"var(--wd-red)" } : {}}
                    onClick={() => setRejectNote(r)}>{r}</button>
                ))}
              </div>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

// ─── Table View ───────────────────────────────────────────────────────────────
function TableView({ docs, onPreview, onVerify, onReject, onNavigate, bulkSelected, onToggleBulk, activeTab,isKam }) {
  return (
    <div className="wd-card">
      <div className="wd-table-wrap">
        <table className="wd-table">
          <thead>
            <tr>
              {activeTab === "PENDING" && <th style={{ width:40 }}></th>}
              <th>DOCUMENT</th>
              <th>CASE</th>
              <th>CUSTOMER</th>
              <th>FILE</th>
              <th>STATUS</th>
              <th>UPLOADED</th>
              <th>SIZE</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {docs.map(doc => {
              const meta  = docMeta(doc.type);
              const isPending = doc.status === "PENDING";
              return (
                <tr key={doc.id} onClick={() => onPreview(doc)}>

                  {/* Bulk checkbox (only pending tab) */}
                  {activeTab === "PENDING" && (
                    <td onClick={e => e.stopPropagation()}>
                      {isPending && !isKam && (
                        <input type="checkbox" checked={bulkSelected.includes(doc.id)}
                          onChange={() => onToggleBulk(doc.id)}
                          style={{ accentColor:"var(--wd-blue)", width:14, height:14, cursor:"pointer" }} />
                      )}
                    </td>
                  )}

                  {/* Doc type with icon */}
                  <td>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{
                        width:34, height:34, borderRadius:8, flexShrink:0,
                        background:`${meta.color}18`, display:"flex",
                        alignItems:"center", justifyContent:"center", fontSize:16,
                      }}>{meta.icon}</div>
                      <span style={{ fontWeight:500, fontSize:13 }}>{doc.type}</span>
                    </div>
                  </td>

                  {/* Case */}
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <button
                        className="wd-btn wd-btn-ghost wd-btn-sm"
                        style={{ fontFamily: "var(--wd-mono)", fontSize: 12, color: "var(--wd-blue)", padding: "3px 8px" }}
                        onClick={e => {
                          e.stopPropagation();
                          onNavigate(doc.service === "legal" ? `/admin/quick-services/legal-cases/${doc.caseNo}` : `/admin/cases/${doc.caseNo}`);
                        }}
                      >
                        {doc.caseNo}
                      </button>
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8,
                        background: doc.service === "legal" ? "rgba(168,85,247,.12)" : "rgba(59,130,246,.12)",
                        color: doc.service === "legal" ? "#a855f7" : "#3b82f6",
                      }}>
                        {doc.service === "legal" ? "LEGAL" : "WORKSHOP"}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: "var(--wd-t3)", fontFamily: "var(--wd-mono)", marginTop: 2 }}>{doc.vehicleNo}</div>
                  </td>

                  {/* Customer */}
                  <td>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <Avatar name={doc.customerName} size={26} />
                      <span style={{ fontSize:13 }}>{doc.customerName}</span>
                    </div>
                  </td>

                  {/* Filename */}
                  <td style={{ fontFamily:"var(--wd-mono)", fontSize:11, color:"var(--wd-t2)", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {doc.fileName}
                  </td>

                  {/* Status */}
                  <td>
                    <Badge variant={docBadge(doc.status)}>{doc.status}</Badge>
                    {doc.remarks && (
                      <div style={{ fontSize:10, color:"var(--wd-red)", marginTop:3, maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={doc.remarks}>
                        {doc.remarks}
                      </div>
                    )}
                  </td>

                  {/* Time */}
                  <td style={{ fontSize:12, color:"var(--wd-t3)", whiteSpace:"nowrap" }}>{formatDateTime(doc.uploadedAt)}</td>

                  {/* Size */}
                  <td style={{ fontSize:12, color:"var(--wd-t3)" }}>{doc.size}</td>

                  {/* Actions */}
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                      <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => onPreview(doc)}>
                        <EyeIcon /> View
                      </button>
                      <button className="wd-btn wd-btn-ghost wd-btn-sm" title="Download">
                        <DownloadIcon />
                      </button>
                      {isPending && !isKam &&  (
                        <>
                          <button className="wd-btn wd-btn-success wd-btn-sm"
                            onClick={() => onVerify(doc.id)}>✓</button>
                          <button className="wd-btn wd-btn-danger wd-btn-sm"
                            onClick={() => onReject(doc)}>✗</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Grid View ────────────────────────────────────────────────────────────────
function GridView({ docs, onPreview, onVerify, onReject, onNavigate, isKam }) {
  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:14 }}>
      {docs.map(doc => {
        const meta = docMeta(doc.type);
        const isPending = doc.status === "PENDING";
        return (
          <div key={doc.id} className="wd-card"
            style={{ padding:16, cursor:"pointer", transition:"border-color .15s" }}
            onClick={() => onPreview(doc)}
            onMouseEnter={e => e.currentTarget.style.borderColor = "var(--wd-blue)"}
            onMouseLeave={e => e.currentTarget.style.borderColor = "var(--wd-border)"}
          >
            {/* Top row */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{
                  width:42, height:42, borderRadius:10, flexShrink:0,
                  background:`${meta.color}18`, display:"flex",
                  alignItems:"center", justifyContent:"center", fontSize:20,
                }}>{meta.icon}</div>
                <div>
                  <div style={{ fontWeight:600, fontSize:13, color:"var(--wd-t1)", lineHeight:1.3 }}>{doc.type}</div>
                  <button className="wd-btn wd-btn-ghost wd-btn-sm"
                    style={{ fontFamily:"var(--wd-mono)", fontSize:11, color:"var(--wd-blue)", padding:"1px 6px", marginTop:2 }}
                    onClick={e => {
                      e.stopPropagation();
                      onNavigate(doc.service === "legal" ? `/admin/quick-services/legal-cases/${doc.caseNo}` : `/admin/cases/${doc.caseNo}`);
                    }}>
                    {doc.caseNo}
                  </button>
                  <span style={{ fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 7,
                    background: doc.service === "legal" ? "rgba(168,85,247,.12)" : "rgba(59,130,246,.12)",
                    color: doc.service === "legal" ? "#a855f7" : "#3b82f6" }}>
                    {doc.service === "legal" ? "LEGAL" : "WKSP"}
                  </span>
                </div>
              </div>
              <Badge variant={docBadge(doc.status)}>{doc.status}</Badge>
            </div>

            {/* File name */}
            <div style={{ fontSize:11, color:"var(--wd-t2)", fontFamily:"var(--wd-mono)", marginBottom:4,
              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {doc.fileName}
            </div>

            {/* Customer + meta */}
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
              <Avatar name={doc.customerName} size={20} />
              <span style={{ fontSize:12, color:"var(--wd-t2)" }}>{doc.customerName}</span>
            </div>
            <div style={{ fontSize:11, color:"var(--wd-t3)", marginBottom:doc.remarks ? 6 : 12 }}>
              {doc.size} · {formatDateTime(doc.uploadedAt)}
            </div>

            {/* Reject reason */}
            {doc.remarks && (
              <div style={{ fontSize:11, color:"var(--wd-red)", padding:"4px 8px", background:"rgba(239,68,68,.08)", borderRadius:6, marginBottom:10, lineHeight:1.4 }}>
                ✗ {doc.remarks}
              </div>
            )}

            {/* Actions */}
            <div className="wd-divider" style={{ margin:"10px 0" }} />
            <div style={{ display:"flex", gap:6 }} onClick={e => e.stopPropagation()}>
              <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ flex:1, justifyContent:"center" }} onClick={() => onPreview(doc)}>
                <EyeIcon /> Preview
              </button>
              <button className="wd-btn wd-btn-ghost wd-btn-icon wd-btn-sm" title="Download"><DownloadIcon /></button>
              {isPending && !isKam && (
                <>
                  <button className="wd-btn wd-btn-success wd-btn-sm" onClick={() => onVerify(doc.id)}>✓</button>
                  <button className="wd-btn wd-btn-danger wd-btn-sm" onClick={() => onReject(doc)}>✗</button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Preview Modal ────────────────────────────────────────────────────────────
function PreviewModal({ doc, onClose, onVerify, onReject, onNavigate, isKam }) {
  if (!doc) return null;
  const meta = docMeta(doc.type);
  const isImage = /\.(jpg|jpeg|png|webp)$/i.test(doc.fileName);

  return (
    <Modal open={!!doc} onClose={onClose} title="Document Preview" maxWidth={640}
      footer={
        <div style={{ display:"flex", justifyContent:"space-between", width:"100%", alignItems:"center" }}>
          <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => {
            onClose();
            onNavigate(doc.service === "legal" ? `/admin/quick-services/legal-cases/${doc.caseNo}` : `/admin/cases/${doc.caseNo}`);
          }}>
            <LinkIcon /> Go to Case
          </button>
          <div style={{ display:"flex", gap:8 }}>
            <button className="wd-btn wd-btn-ghost" onClick={onClose}>Close</button>
            {["PENDING", null, undefined].includes(doc.status) && !isKam &&(
              <>
                <button className="wd-btn wd-btn-danger" onClick={() => { onClose(); onReject(doc); }}>✗ Reject</button>
                <button className="wd-btn wd-btn-success" onClick={() => { onVerify(doc.id); onClose(); }}>✓ Verify</button>
              </>
            )}
          </div>
        </div>
      }
    >
      {/* File preview area */}
      <div style={{
        height:220, borderRadius:12, background:"var(--wd-hover)",
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
        border:"1px dashed var(--wd-border-md)", gap:10,
      }}>
        <span style={{ fontSize:48 }}>{meta.icon}</span>
        <span style={{ fontSize:12, color:"var(--wd-t3)" }}>
          {isImage ? "Image preview loads from your backend" : "Document preview loads from your backend"}
        </span>
        {/* 
          To show real preview, replace above with:
          isImage
            ? <img src={`/api/documents/${doc.id}/file`} style={{ maxHeight:"100%", maxWidth:"100%", objectFit:"contain", borderRadius:8 }} />
            : <iframe src={`/api/documents/${doc.id}/file`} style={{ width:"100%", height:"100%", border:"none", borderRadius:8 }} />
        */}
        <button className="wd-btn wd-btn-ghost wd-btn-sm"><DownloadIcon /> Download</button>
      </div>

      {/* Doc details grid */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
        <InfoRow label="Document Type"  value={doc.type} />
        <InfoRow label="Case No"        value={doc.caseNo} mono />
        <InfoRow label="Customer"       value={doc.customerName} />
        <InfoRow label="Vehicle No"     value={doc.vehicleNo} mono />
        <InfoRow label="File Name"      value={doc.fileName} mono />
        <InfoRow label="File Size"      value={doc.size} />
        <InfoRow label="Uploaded At"    value={formatDateTime(doc.uploadedAt)} />
        <InfoRow label="Verified By"    value={doc.verifiedBy || "—"} />
      </div>

      {/* Status */}
      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 16px", background:"var(--wd-hover)", borderRadius:10 }}>
        <span style={{ fontSize:12, color:"var(--wd-t3)", fontWeight:600, textTransform:"uppercase", letterSpacing:".05em" }}>Status</span>
        <Badge variant={docBadge(doc.status)}>{doc.status}</Badge>
        {doc.remarks && (
          <span style={{ fontSize:12, color:"var(--wd-red)", marginLeft:8 }}>— {doc.remarks}</span>
        )}
      </div>
    </Modal>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────
function EyeIcon()      { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>; }
function DownloadIcon() { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>; }
function GridIcon()     { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>; }
function ListIcon()     { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>; }
function LinkIcon()     { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>; }